import { Link } from "react-router-dom";
import { toast } from "sonner";
import { HardDriveDownload, Lock } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import { useOffline } from "@/lib/offline/useOffline";
import { download, remove, pendingFor } from "@/lib/offline/engine";

export const OfflineAccess = ({ universe }) => {
  const s = useOffline();
  const uid = universe.id;
  const m = s.meta[uid];
  const busy = s.busy[uid];
  const n = s.outbox.filter((e) => e.uid === uid).length;
  const onToggle = async (v) => {
    if (v) {
      try { await download(uid); toast.success(`${universe.name} is available offline on this device`); window.dispatchEvent(new Event("lf-open-sync")); }
      catch (e) { console.error("offline download", e); toast.error("Couldn't download this universe. Check your connection and try again."); }
      return;
    }
    if (pendingFor(uid).length && !window.confirm(`You have ${pendingFor(uid).length} change(s) that haven't been pushed. Remove the offline copy and discard them?`)) return;
    await remove(uid);
    toast.success("Offline copy removed from this device");
  };
  return (
    <section className="rounded-md border border-[#2D3442] bg-[#13171F] p-6" data-testid="settings-offline">
      <h2 className="flex items-center gap-2 font-display text-2xl text-[#F3EBDD]"><HardDriveDownload className="h-5 w-5 text-[#F59E0B]" />Offline access</h2>
      <p className="mt-1 mb-4 text-sm text-[#A8A090]">Keep a full copy of this universe on this device. Write notes, chapters, timelines, maps and languages with no internet, then push your changes when you choose.</p>
      {!s.eligible ? (
        <p className="flex items-center gap-2 text-sm text-[#FBBF24]" data-testid="offline-upsell"><Lock className="h-4 w-4" />Offline access is part of Pro and Lifetime plans. <Link to="/pricing" className="underline" data-testid="offline-upsell-link">See plans</Link></p>
      ) : (
        <>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-[#F3EBDD]">Make available offline</div>
              <div className="text-xs text-[#A8A090]" data-testid="offline-status">{busy ? `Downloading… ${busy.done} of ${busy.total}` : m ? `Saved on this device · ${new Date(m.at).toLocaleString()}${n ? ` · ${n} change${n === 1 ? "" : "s"} to push` : ""}` : "Not saved on this device"}</div>
            </div>
            <Switch checked={!!m || !!busy} disabled={!!busy || (!m && !navigator.onLine)} onCheckedChange={onToggle} data-testid="offline-toggle" />
          </div>
          {busy && <Progress value={(busy.done / Math.max(busy.total, 1)) * 100} className="mt-3 h-1.5" data-testid="offline-progress" />}
          {m && <button onClick={() => window.dispatchEvent(new Event("lf-open-sync"))} className="mt-3 text-xs text-[#F59E0B] hover:underline" data-testid="offline-open-sync">Open sync panel</button>}
        </>
      )}
    </section>
  );
};
